/* ================================================================
   BB Product UX — v4.0
   Belle Bonjour · Product Page Intelligence Engine
   Fixes: no async/await, debounced detection, lastHandledVariantId guard,
          robust multi-signal variant detection, promise-based Klaviyo
   ================================================================ */

(function () {
  'use strict';

  var IS_AR    = document.documentElement.lang === 'ar';
  var CURRENCY = 'AED';

  var _lastHandledVariantId = null;
  var _debounceTimer        = null;

  /* ── Utility ────────────────────────────────────────────────────────── */

  function tr(en, ar) { return IS_AR ? ar : en; }

  function fmt(cents) { return CURRENCY + ' ' + Math.round(cents / 100); }

  function debounce(fn, ms) {
    return function () {
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(fn, ms);
    };
  }

  function getProductData() {
    var el = document.getElementById('bb-product-data');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getCurrentVariantId() {
    // 1. URL query param (most reliable for OS2)
    try {
      var p = new URL(window.location.href).searchParams.get('variant');
      if (p) return parseInt(p, 10);
    } catch (e) {}
    // 2. Checked radio input
    var checked = document.querySelector('input[name="id"]:checked');
    if (checked && checked.value) return parseInt(checked.value, 10);
    // 3. Hidden input
    var hidden = document.querySelector('input[name="id"][type="hidden"]');
    if (hidden && hidden.value) return parseInt(hidden.value, 10);
    // 4. Select
    var sel = document.querySelector('select[name="id"]');
    if (sel && sel.value) return parseInt(sel.value, 10);
    return null;
  }

  function findVariant(id) {
    var product = getProductData();
    if (!product || !id) return null;
    var numId = parseInt(id, 10);
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].id === numId) return product.variants[i];
    }
    return null;
  }

  /* ── Core dispatch ──────────────────────────────────────────────── */

  function detectAndApply() {
    var id = getCurrentVariantId();
    if (!id) return;
    if (id === _lastHandledVariantId) return; // no change, skip
    var variant = findVariant(id);
    if (!variant) return;
    _lastHandledVariantId = id;
    onVariantChange(variant);
  }

  var debouncedDetect = debounce(detectAndApply, 60);

  function onVariantChange(variant) {
    updateStockStatus(variant);
    toggleNotifyMe(variant);
    trackVariantSelect(variant);
  }

  /* ── Stock status ──────────────────────────────────────────────── */

  function updateStockStatus(variant) {
    var el = document.querySelector('[data-bb-stock-status]');
    if (!el) return;
    var dot, msg;
    if (!variant.available) {
      dot = 'out'; msg = tr('Sold Out', 'نفذت الكمية');
      el.dataset.status = 'sold_out';
    } else if (
      variant.inventory_management === 'shopify' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= 5
    ) {
      dot = 'low';
      msg = IS_AR ? variant.inventory_quantity + ' قطع متبقية فقط' : 'Only ' + variant.inventory_quantity + ' left';
      el.dataset.status = 'low_stock';
    } else {
      dot = 'in'; msg = tr('In Stock', 'متوفر');
      el.dataset.status = 'in_stock';
    }
    el.innerHTML =
      '<span class="bb-stock-dot bb-stock-dot--' + dot + '" aria-hidden="true"></span>' +
      '<span>' + msg + '</span>';
  }

  /* ── Notify Me toggle ──────────────────────────────────────────── */

  function toggleNotifyMe(variant) {
    var el = document.querySelector('[data-bb-notify-me]');
    if (!el) return;
    if (!variant.available) {
      el.style.display = '';
      el.dataset.variantId = String(variant.id);
      var hidden = el.querySelector('[name="variant_id"]');
      if (hidden) hidden.value = variant.id;
      var form = el.querySelector('[data-bb-notify-form]');
      if (form) form.dataset.variantId = String(variant.id);
    } else {
      el.style.display = 'none';
    }
  }

  /* ── Variant detection — 7 signals, debounced ─────────────────────── */

  function initVariantListeners() {

    // S1: click on variant option buttons / swatches / labels
    document.addEventListener('click', function (e) {
      if (e.target.closest(
        '.variant-option__button-label, [data-option-value], swatches-component label, ' +
        '.product-form__input label, [class*="variant-option"] label'
      )) debouncedDetect();
    });

    // S2: change on any [name="id"] input or select
    document.addEventListener('change', function (e) {
      var tgt = e.target;
      if (tgt && (tgt.name === 'id' ||
        (tgt.form && tgt.form.querySelector('[name="id"]')))) {
        detectAndApply();
      }
    });

    // S3: MutationObserver on ATC submit button
    //     (disabled / class changes are the most reliable OS2 signal)
    (function () {
      var atc = document.querySelector(
        '.product-form__submit, ' +
        'product-form-component [type="submit"], ' +
        '[data-type="add-to-cart-form"] [type="submit"]'
      );
      if (atc) {
        new MutationObserver(debouncedDetect)
          .observe(atc, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'] });
      }
    }());

    // S4: MutationObserver on hidden [name="id"] input value attribute
    (function () {
      var inp = document.querySelector('input[name="id"][type="hidden"]');
      if (inp) {
        new MutationObserver(debouncedDetect)
          .observe(inp, { attributes: true, attributeFilter: ['value'] });
      }
    }());

    // S5: popstate (browser back / forward)
    window.addEventListener('popstate', debouncedDetect);

    // S6: pushState intercept (URL-updating themes)
    (function () {
      try {
        var orig = history.pushState;
        history.pushState = function (state, title, url) {
          orig.call(history, state, title, url);
          debouncedDetect();
        };
      } catch (e) {}
    }());

    // S7: short polling (500ms × 10 = 5s) — catches async theme updates
    var polls = 0;
    var pollTimer = setInterval(function () {
      detectAndApply();
      if (++polls >= 10) clearInterval(pollTimer);
    }, 500);
  }

  /* ── Notify Me submission (promise-based, no async/await) ───────── */

  function getKlaviyoId() {
    var scripts = document.querySelectorAll('script[src*="klaviyo"]');
    for (var i = 0; i < scripts.length; i++) {
      var m = scripts[i].src.match(/company_id=([A-Za-z0-9]+)/);
      if (m) return m[1];
    }
    if (window.__kl_page_settings && window.__kl_page_settings.company_id) {
      return window.__kl_page_settings.company_id;
    }
    return null;
  }

  function showSuccess(form) {
    var msg = tr(
      "We'll notify you the moment it's back.",
      'سنُبلغكِ فور عودة المنتج.'
    );
    var wrapper = form.closest('.bb-notify-me__inner');
    if (wrapper) {
      wrapper.innerHTML =
        '<p class="bb-notify-me__success">' +
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" ' +
          'stroke="currentColor" stroke-width="2.5" aria-hidden="true">' +
          '<path d="M20 6L9 17l-5-5"/>' +
        '</svg> ' + msg +
        '</p>';
    }
  }

  function showError(form, msg) {
    var el = form.querySelector('.bb-notify-me__error');
    if (el) { el.textContent = msg; el.style.display = ''; }
  }

  function submitNotifyMe(form, email, variantId) {
    var btn = form.querySelector('[type="submit"]');
    var btnDefault = btn ? (btn.dataset.defaultText || btn.textContent.trim()) : '';
    if (btn) { btn.disabled = true; btn.textContent = '...'; }

    var product    = getProductData();
    var title      = product ? product.title : (document.title || '');
    var klaviyoId  = getKlaviyoId();

    function onDone() {
      showSuccess(form);
      if (window.gtag) window.gtag('event', 'notify_me_submit', { variant_id: variantId });
      if (window.fbq) window.fbq('trackCustom', 'NotifyMe', { variant_id: variantId });
    }

    function onFallback() {
      var msg = IS_AR
        ? 'مرحباً، أريد الإبلاغ عند توفر: ' + title + ' (' + email + ')'
        : 'Please notify me when back in stock: ' + title + ' — ' + email;
      window.open('https://wa.me/971588487700?text=' + encodeURIComponent(msg), '_blank');
      onDone();
    }

    function restoreBtn() {
      if (btn && btn.disabled) {
        btn.disabled = false;
        btn.textContent = btnDefault;
      }
    }

    if (klaviyoId) {
      fetch(
        'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + klaviyoId,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'revision': '2023-02-22' },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email: email } } }
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: '$shopify:::$default:::' + variantId
                  }
                }
              }
            }
          })
        }
      ).then(function (res) {
        if (res.ok || res.status === 202) { onDone(); }
        else { onFallback(); }
      }).catch(function () {
        onFallback();
      }).then(function () {
        // Final cleanup regardless of path
        restoreBtn();
      });
    } else {
      onFallback();
      restoreBtn();
    }
  }

  function initNotifyMe() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-bb-notify-form]');
      if (!form) return;
      e.preventDefault();

      var emailEl   = form.querySelector('[name="email"]');
      var email     = emailEl ? emailEl.value.trim() : '';
      var variantId = form.dataset.variantId ||
                      ((form.querySelector('[name="variant_id"]') || {}).value) || '';

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showError(form, tr('Please enter a valid email address.', 'أدخلي بريدًا إلكترونيًا صحيحًا.'));
        return;
      }

      submitNotifyMe(form, email, variantId);
    });
  }

  /* ── Analytics ──────────────────────────────────────────────────── */

  function trackVariantSelect(variant) {
    var product = getProductData();
    if (!product || !window.gtag) return;
    window.gtag('event', 'select_item', {
      item_list_id: 'product_page',
      items: [{ item_id: variant.sku || String(variant.id), item_name: variant.title, price: variant.price / 100 }]
    });
  }

  function initAnalytics() {
    var product = getProductData();
    if (product) {
      var v     = product.variants[0];
      var price = v ? v.price / 100 : 0;
      var item  = { item_id: String(product.id), item_name: product.title, item_category: product.type, price: price, quantity: 1 };
      if (window.gtag) window.gtag('event', 'view_item', { currency: CURRENCY, value: price, items: [item] });
      if (window.fbq)  window.fbq('track', 'ViewContent', { content_ids: [String(product.id)], content_name: product.title, content_type: 'product', value: price, currency: CURRENCY });
      if (window.ttq)  window.ttq.track('ViewContent', { contents: [{ content_id: String(product.id), content_name: product.title, price: price }], value: price, currency: CURRENCY });
    }

    // Add to cart click
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.product-form__submit, #ProductSubmitButton, .add-to-cart-button');
      if (!btn || btn.disabled) return;
      var p = getProductData();
      if (!p) return;
      var vr    = findVariant(getCurrentVariantId()) || p.variants[0];
      var price = vr ? vr.price / 100 : 0;
      if (window.gtag) window.gtag('event', 'add_to_cart', { currency: CURRENCY, value: price, items: [{ item_id: String(p.id), item_name: p.title, item_category: p.type, price: price, quantity: 1 }] });
      if (window.fbq)  window.fbq('track', 'AddToCart', { content_ids: [String(p.id)], content_name: p.title, value: price, currency: CURRENCY });
      if (window.ttq)  window.ttq.track('AddToCart', { contents: [{ content_id: String(p.id), content_name: p.title, price: price }], value: price, currency: CURRENCY });
    });

    // Scroll depth
    var scrollFired = {};
    window.addEventListener('scroll', function () {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h <= 0) return;
      var pct = Math.round((window.scrollY / h) * 100);
      [25, 50, 75, 90].forEach(function (d) {
        if (pct >= d && !scrollFired[d]) {
          scrollFired[d] = true;
          if (window.gtag) window.gtag('event', 'scroll', { percent_scrolled: d });
        }
      });
    }, { passive: true });
  }

  /* ── Boot ─────────────────────────────────────────────────────────── */

  function boot() {
    if (!document.getElementById('bb-product-data')) return; // guard: product page only

    initNotifyMe();
    initAnalytics();
    initVariantListeners();

    // Apply initial state immediately
    var product = getProductData();
    if (product) {
      var initId = getCurrentVariantId();
      var initVariant =
        findVariant(initId) ||
        product.variants.filter(function (v) { return v.available; })[0] ||
        product.variants[0];

      if (initVariant) {
        _lastHandledVariantId = initVariant.id;
        onVariantChange(initVariant);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
